/**
 * Phone number validation utility
 * Maps country codes to valid phone number lengths and validation rules
 */

export type CountryCode = `+${string}`;

/**
 * Phone number validation configuration for each country code
 * Format: country code -> { minLength, maxLength, pattern? }
 * Length is for the number AFTER the country code (not including country code)
 */
const PHONE_VALIDATION_RULES: Record<CountryCode, { minLength: number; maxLength: number; pattern?: RegExp }> = {
  // North America
  '+1': { minLength: 10, maxLength: 10 }, // US, Canada
  '+1242': { minLength: 7, maxLength: 7 }, // Bahamas
  '+1246': { minLength: 7, maxLength: 7 }, // Barbados
  '+1268': { minLength: 7, maxLength: 7 }, // Antigua and Barbuda
  '+1284': { minLength: 7, maxLength: 7 }, // British Virgin Islands
  '+1340': { minLength: 10, maxLength: 10 }, // US Virgin Islands
  '+1473': { minLength: 7, maxLength: 7 }, // Grenada
  '+1649': { minLength: 10, maxLength: 10 }, // Turks and Caicos
  '+1664': { minLength: 7, maxLength: 7 }, // Montserrat
  '+1670': { minLength: 10, maxLength: 10 }, // Northern Mariana Islands
  '+1671': { minLength: 10, maxLength: 10 }, // Guam
  '+1684': { minLength: 10, maxLength: 10 }, // American Samoa
  '+1758': { minLength: 7, maxLength: 7 }, // Saint Lucia
  '+1767': { minLength: 7, maxLength: 7 }, // Dominica
  '+1784': { minLength: 7, maxLength: 7 }, // Saint Vincent
  '+1787': { minLength: 10, maxLength: 10 }, // Puerto Rico
  '+1809': { minLength: 10, maxLength: 10 }, // Dominican Republic
  '+1829': { minLength: 10, maxLength: 10 }, // Dominican Republic
  '+1849': { minLength: 10, maxLength: 10 }, // Dominican Republic
  '+1868': { minLength: 7, maxLength: 7 }, // Trinidad and Tobago
  '+1869': { minLength: 10, maxLength: 10 }, // Saint Kitts and Nevis
  '+1876': { minLength: 7, maxLength: 7 }, // Jamaica

  // Europe
  '+30': { minLength: 10, maxLength: 10 }, // Greece
  '+31': { minLength: 9, maxLength: 9 }, // Netherlands
  '+32': { minLength: 9, maxLength: 9 }, // Belgium
  '+33': { minLength: 9, maxLength: 9 }, // France
  '+34': { minLength: 9, maxLength: 9 }, // Spain
  '+36': { minLength: 9, maxLength: 9 }, // Hungary
  '+39': { minLength: 9, maxLength: 10 }, // Italy
  '+40': { minLength: 9, maxLength: 9 }, // Romania
  '+41': { minLength: 9, maxLength: 9 }, // Switzerland
  '+43': { minLength: 10, maxLength: 13 }, // Austria
  '+44': { minLength: 10, maxLength: 10 }, // UK
  '+45': { minLength: 8, maxLength: 8 }, // Denmark
  '+46': { minLength: 9, maxLength: 9 }, // Sweden
  '+47': { minLength: 8, maxLength: 8 }, // Norway
  '+48': { minLength: 9, maxLength: 9 }, // Poland
  '+49': { minLength: 10, maxLength: 11 }, // Germany
  '+351': { minLength: 9, maxLength: 9 }, // Portugal
  '+352': { minLength: 9, maxLength: 9 }, // Luxembourg
  '+353': { minLength: 9, maxLength: 9 }, // Ireland
  '+354': { minLength: 7, maxLength: 7 }, // Iceland
  '+355': { minLength: 9, maxLength: 9 }, // Albania
  '+356': { minLength: 8, maxLength: 8 }, // Malta
  '+357': { minLength: 8, maxLength: 8 }, // Cyprus
  '+358': { minLength: 5, maxLength: 12 }, // Finland
  '+359': { minLength: 9, maxLength: 9 }, // Bulgaria
  '+370': { minLength: 8, maxLength: 8 }, // Lithuania
  '+371': { minLength: 8, maxLength: 8 }, // Latvia
  '+372': { minLength: 7, maxLength: 8 }, // Estonia
  '+373': { minLength: 8, maxLength: 8 }, // Moldova
  '+374': { minLength: 8, maxLength: 8 }, // Armenia
  '+375': { minLength: 9, maxLength: 9 }, // Belarus
  '+376': { minLength: 6, maxLength: 6 }, // Andorra
  '+377': { minLength: 9, maxLength: 9 }, // Monaco
  '+378': { minLength: 10, maxLength: 10 }, // San Marino
  '+380': { minLength: 9, maxLength: 9 }, // Ukraine
  '+381': { minLength: 9, maxLength: 9 }, // Serbia
  '+382': { minLength: 8, maxLength: 8 }, // Montenegro
  '+383': { minLength: 8, maxLength: 8 }, // Kosovo
  '+385': { minLength: 8, maxLength: 9 }, // Croatia
  '+386': { minLength: 8, maxLength: 8 }, // Slovenia
  '+387': { minLength: 8, maxLength: 8 }, // Bosnia and Herzegovina
  '+389': { minLength: 8, maxLength: 8 }, // North Macedonia
  '+420': { minLength: 9, maxLength: 9 }, // Czech Republic
  '+421': { minLength: 9, maxLength: 9 }, // Slovakia
  '+423': { minLength: 7, maxLength: 7 }, // Liechtenstein

  // Asia
  '+7': { minLength: 10, maxLength: 10 }, // Russia, Kazakhstan
  '+60': { minLength: 9, maxLength: 10 }, // Malaysia
  '+61': { minLength: 9, maxLength: 9 }, // Australia
  '+62': { minLength: 9, maxLength: 11 }, // Indonesia
  '+63': { minLength: 10, maxLength: 10 }, // Philippines
  '+64': { minLength: 8, maxLength: 10 }, // New Zealand
  '+65': { minLength: 8, maxLength: 8 }, // Singapore
  '+66': { minLength: 9, maxLength: 9 }, // Thailand
  '+81': { minLength: 10, maxLength: 10 }, // Japan
  '+82': { minLength: 9, maxLength: 10 }, // South Korea
  '+84': { minLength: 9, maxLength: 10 }, // Vietnam
  '+86': { minLength: 11, maxLength: 11 }, // China
  '+90': { minLength: 10, maxLength: 10 }, // Turkey
  '+91': { minLength: 10, maxLength: 10 }, // India
  '+92': { minLength: 10, maxLength: 10 }, // Pakistan
  '+93': { minLength: 9, maxLength: 9 }, // Afghanistan
  '+94': { minLength: 9, maxLength: 9 }, // Sri Lanka
  '+95': { minLength: 8, maxLength: 10 }, // Myanmar
  '+98': { minLength: 10, maxLength: 10 }, // Iran
  '+880': { minLength: 10, maxLength: 10 }, // Bangladesh
  '+960': { minLength: 7, maxLength: 7 }, // Maldives
  '+961': { minLength: 7, maxLength: 8 }, // Lebanon
  '+962': { minLength: 9, maxLength: 9 }, // Jordan
  '+963': { minLength: 9, maxLength: 9 }, // Syria
  '+964': { minLength: 10, maxLength: 10 }, // Iraq
  '+965': { minLength: 8, maxLength: 8 }, // Kuwait
  '+966': { minLength: 9, maxLength: 9 }, // Saudi Arabia
  '+967': { minLength: 9, maxLength: 9 }, // Yemen
  '+968': { minLength: 8, maxLength: 8 }, // Oman
  '+970': { minLength: 9, maxLength: 9 }, // Palestine
  '+971': { minLength: 9, maxLength: 9 }, // UAE
  '+972': { minLength: 9, maxLength: 9 }, // Israel
  '+973': { minLength: 8, maxLength: 8 }, // Bahrain
  '+974': { minLength: 8, maxLength: 8 }, // Qatar
  '+976': { minLength: 8, maxLength: 8 }, // Mongolia
  '+977': { minLength: 10, maxLength: 10 }, // Nepal
  '+992': { minLength: 9, maxLength: 9 }, // Tajikistan
  '+993': { minLength: 8, maxLength: 8 }, // Turkmenistan
  '+994': { minLength: 9, maxLength: 9 }, // Azerbaijan
  '+995': { minLength: 9, maxLength: 9 }, // Georgia
  '+996': { minLength: 9, maxLength: 9 }, // Kyrgyzstan
  '+998': { minLength: 9, maxLength: 9 }, // Uzbekistan

  // Africa
  '+20': { minLength: 10, maxLength: 10 }, // Egypt
  '+27': { minLength: 9, maxLength: 9 }, // South Africa
  '+212': { minLength: 9, maxLength: 9 }, // Morocco
  '+213': { minLength: 9, maxLength: 9 }, // Algeria
  '+216': { minLength: 8, maxLength: 8 }, // Tunisia
  '+218': { minLength: 9, maxLength: 9 }, // Libya
  '+220': { minLength: 7, maxLength: 7 }, // Gambia
  '+221': { minLength: 9, maxLength: 9 }, // Senegal
  '+222': { minLength: 8, maxLength: 8 }, // Mauritania
  '+223': { minLength: 8, maxLength: 8 }, // Mali
  '+224': { minLength: 9, maxLength: 9 }, // Guinea
  '+225': { minLength: 10, maxLength: 10 }, // Ivory Coast
  '+226': { minLength: 8, maxLength: 8 }, // Burkina Faso
  '+227': { minLength: 8, maxLength: 8 }, // Niger
  '+228': { minLength: 8, maxLength: 8 }, // Togo
  '+229': { minLength: 8, maxLength: 8 }, // Benin
  '+230': { minLength: 7, maxLength: 7 }, // Mauritius
  '+231': { minLength: 7, maxLength: 8 }, // Liberia
  '+232': { minLength: 8, maxLength: 8 }, // Sierra Leone
  '+233': { minLength: 9, maxLength: 9 }, // Ghana
  '+234': { minLength: 10, maxLength: 10 }, // Nigeria
  '+235': { minLength: 8, maxLength: 8 }, // Chad
  '+236': { minLength: 8, maxLength: 8 }, // Central African Republic
  '+237': { minLength: 9, maxLength: 9 }, // Cameroon
  '+238': { minLength: 7, maxLength: 7 }, // Cape Verde
  '+239': { minLength: 7, maxLength: 7 }, // São Tomé and Príncipe
  '+240': { minLength: 9, maxLength: 9 }, // Equatorial Guinea
  '+241': { minLength: 8, maxLength: 8 }, // Gabon
  '+242': { minLength: 9, maxLength: 9 }, // Republic of the Congo
  '+243': { minLength: 9, maxLength: 9 }, // Democratic Republic of the Congo
  '+244': { minLength: 9, maxLength: 9 }, // Angola
  '+245': { minLength: 7, maxLength: 7 }, // Guinea-Bissau
  '+246': { minLength: 7, maxLength: 7 }, // British Indian Ocean Territory
  '+248': { minLength: 7, maxLength: 7 }, // Seychelles
  '+249': { minLength: 9, maxLength: 9 }, // Sudan
  '+250': { minLength: 9, maxLength: 9 }, // Rwanda
  '+251': { minLength: 9, maxLength: 9 }, // Ethiopia
  '+252': { minLength: 8, maxLength: 8 }, // Somalia
  '+253': { minLength: 8, maxLength: 8 }, // Djibouti
  '+254': { minLength: 9, maxLength: 9 }, // Kenya
  '+255': { minLength: 9, maxLength: 9 }, // Tanzania
  '+256': { minLength: 9, maxLength: 9 }, // Uganda
  '+257': { minLength: 8, maxLength: 8 }, // Burundi
  '+258': { minLength: 9, maxLength: 9 }, // Mozambique
  '+260': { minLength: 9, maxLength: 9 }, // Zambia
  '+261': { minLength: 9, maxLength: 9 }, // Madagascar
  '+262': { minLength: 9, maxLength: 9 }, // Réunion, Mayotte
  '+263': { minLength: 9, maxLength: 9 }, // Zimbabwe
  '+264': { minLength: 9, maxLength: 9 }, // Namibia
  '+265': { minLength: 9, maxLength: 9 }, // Malawi
  '+266': { minLength: 8, maxLength: 8 }, // Lesotho
  '+267': { minLength: 7, maxLength: 8 }, // Botswana
  '+268': { minLength: 8, maxLength: 8 }, // Eswatini
  '+269': { minLength: 7, maxLength: 7 }, // Comoros
  '+290': { minLength: 4, maxLength: 4 }, // Saint Helena
  '+291': { minLength: 7, maxLength: 7 }, // Eritrea
  '+297': { minLength: 7, maxLength: 7 }, // Aruba
  '+298': { minLength: 6, maxLength: 6 }, // Faroe Islands
  '+299': { minLength: 6, maxLength: 6 }, // Greenland

  // Latin America & Caribbean
  '+51': { minLength: 9, maxLength: 9 }, // Peru
  '+52': { minLength: 10, maxLength: 10 }, // Mexico
  '+53': { minLength: 8, maxLength: 8 }, // Cuba
  '+54': { minLength: 10, maxLength: 10 }, // Argentina
  '+55': { minLength: 10, maxLength: 11 }, // Brazil
  '+56': { minLength: 9, maxLength: 9 }, // Chile
  '+57': { minLength: 10, maxLength: 10 }, // Colombia
  '+58': { minLength: 10, maxLength: 10 }, // Venezuela
  '+59': { minLength: 10, maxLength: 10 }, // Guadeloupe, Martinique
  '+501': { minLength: 7, maxLength: 7 }, // Belize
  '+502': { minLength: 8, maxLength: 8 }, // Guatemala
  '+503': { minLength: 8, maxLength: 8 }, // El Salvador
  '+504': { minLength: 8, maxLength: 8 }, // Honduras
  '+505': { minLength: 8, maxLength: 8 }, // Nicaragua
  '+506': { minLength: 8, maxLength: 8 }, // Costa Rica
  '+507': { minLength: 8, maxLength: 8 }, // Panama
  '+508': { minLength: 6, maxLength: 6 }, // Saint Pierre and Miquelon
  '+509': { minLength: 8, maxLength: 8 }, // Haiti
  '+591': { minLength: 8, maxLength: 8 }, // Bolivia
  '+592': { minLength: 7, maxLength: 7 }, // Guyana
  '+593': { minLength: 9, maxLength: 9 }, // Ecuador
  '+595': { minLength: 9, maxLength: 9 }, // Paraguay
  '+597': { minLength: 7, maxLength: 7 }, // Suriname
  '+598': { minLength: 8, maxLength: 8 }, // Uruguay

  // Oceania & Pacific
  '+61': { minLength: 9, maxLength: 9 }, // Australia
  '+64': { minLength: 8, maxLength: 10 }, // New Zealand
  '+670': { minLength: 8, maxLength: 8 }, // East Timor
  '+672': { minLength: 5, maxLength: 6 }, // Australian External Territories
  '+673': { minLength: 7, maxLength: 7 }, // Brunei
  '+674': { minLength: 7, maxLength: 7 }, // Nauru
  '+675': { minLength: 8, maxLength: 8 }, // Papua New Guinea
  '+676': { minLength: 7, maxLength: 7 }, // Tonga
  '+677': { minLength: 7, maxLength: 7 }, // Solomon Islands
  '+678': { minLength: 7, maxLength: 7 }, // Vanuatu
  '+679': { minLength: 7, maxLength: 7 }, // Fiji
  '+680': { minLength: 7, maxLength: 7 }, // Palau
  '+681': { minLength: 6, maxLength: 6 }, // Wallis and Futuna
  '+682': { minLength: 5, maxLength: 5 }, // Cook Islands
  '+683': { minLength: 4, maxLength: 4 }, // Niue
  '+685': { minLength: 7, maxLength: 7 }, // Samoa
  '+686': { minLength: 8, maxLength: 8 }, // Kiribati
  '+687': { minLength: 6, maxLength: 6 }, // New Caledonia
  '+688': { minLength: 5, maxLength: 5 }, // Tuvalu
  '+689': { minLength: 8, maxLength: 8 }, // French Polynesia
  '+691': { minLength: 7, maxLength: 7 }, // Micronesia
  '+692': { minLength: 7, maxLength: 7 }, // Marshall Islands
  '+850': { minLength: 8, maxLength: 11 }, // North Korea
  '+852': { minLength: 8, maxLength: 8 }, // Hong Kong
  '+853': { minLength: 8, maxLength: 8 }, // Macau
  '+855': { minLength: 8, maxLength: 9 }, // Cambodia
  '+856': { minLength: 8, maxLength: 10 }, // Laos
  '+886': { minLength: 9, maxLength: 9 }, // Taiwan

  // Middle East
  '+90': { minLength: 10, maxLength: 10 }, // Turkey
  '+92': { minLength: 10, maxLength: 10 }, // Pakistan
  '+93': { minLength: 9, maxLength: 9 }, // Afghanistan
  '+94': { minLength: 9, maxLength: 9 }, // Sri Lanka
  '+95': { minLength: 8, maxLength: 10 }, // Myanmar
  '+98': { minLength: 10, maxLength: 10 }, // Iran
  '+961': { minLength: 7, maxLength: 8 }, // Lebanon
  '+962': { minLength: 9, maxLength: 9 }, // Jordan
  '+963': { minLength: 9, maxLength: 9 }, // Syria
  '+964': { minLength: 10, maxLength: 10 }, // Iraq
  '+965': { minLength: 8, maxLength: 8 }, // Kuwait
  '+966': { minLength: 9, maxLength: 9 }, // Saudi Arabia
  '+967': { minLength: 9, maxLength: 9 }, // Yemen
  '+968': { minLength: 8, maxLength: 8 }, // Oman
  '+970': { minLength: 9, maxLength: 9 }, // Palestine
  '+971': { minLength: 9, maxLength: 9 }, // UAE
  '+972': { minLength: 9, maxLength: 9 }, // Israel
  '+973': { minLength: 8, maxLength: 8 }, // Bahrain
  '+974': { minLength: 8, maxLength: 8 }, // Qatar
  '+976': { minLength: 8, maxLength: 8 }, // Mongolia
  '+977': { minLength: 10, maxLength: 10 }, // Nepal
  '+992': { minLength: 9, maxLength: 9 }, // Tajikistan
  '+993': { minLength: 8, maxLength: 8 }, // Turkmenistan
  '+994': { minLength: 9, maxLength: 9 }, // Azerbaijan
  '+995': { minLength: 9, maxLength: 9 }, // Georgia
  '+996': { minLength: 9, maxLength: 9 }, // Kyrgyzstan
  '+998': { minLength: 9, maxLength: 9 }, // Uzbekistan
};

/**
 * Get phone number validation rules for a country code
 */
export function getPhoneValidationRules(countryCode: CountryCode): { minLength: number; maxLength: number } | null {
  return PHONE_VALIDATION_RULES[countryCode] || null;
}

/**
 * Validate phone number based on country code
 * @param phoneNumber - The phone number (without country code)
 * @param countryCode - The country code (e.g., "+33", "+1")
 * @returns Object with isValid flag and error message
 */
export function validatePhoneNumber(
  phoneNumber: string,
  countryCode: CountryCode
): { isValid: boolean; error?: string } {
  // Remove all non-digit characters for validation
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Get validation rules for the country code
  const rules = getPhoneValidationRules(countryCode);

  if (!rules) {
    // If no rules found for country code, use a generic validation
    if (digitsOnly.length < 5) {
      return {
        isValid: false,
        error: 'Phone number must be at least 5 digits',
      };
    }
    if (digitsOnly.length > 15) {
      return {
        isValid: false,
        error: 'Phone number must be at most 15 digits',
      };
    }
    return { isValid: true };
  }

  // Validate length
  if (digitsOnly.length < rules.minLength) {
    return {
      isValid: false,
      error: `Phone number must be at least ${rules.minLength} digits for this country`,
    };
  }

  if (digitsOnly.length > rules.maxLength) {
    return {
      isValid: false,
      error: `Phone number must be at most ${rules.maxLength} digits for this country`,
    };
  }

  // If pattern is provided, validate against it
  if (rules.pattern && !rules.pattern.test(digitsOnly)) {
    return {
      isValid: false,
      error: 'Phone number format is invalid for this country',
    };
  }

  return { isValid: true };
}

/**
 * Get country name from country code for error messages
 */
export function getCountryNameFromCode(countryCode: CountryCode): string {
  // This is a simplified mapping - you could extend this
  const codeToCountry: Record<string, string> = {
    '+1': 'United States/Canada',
    '+33': 'France',
    '+44': 'United Kingdom',
    '+49': 'Germany',
    '+39': 'Italy',
    '+34': 'Spain',
    '+86': 'China',
    '+81': 'Japan',
    '+91': 'India',
    '+52': 'Mexico',
    '+55': 'Brazil',
    '+61': 'Australia',
    // Add more as needed
  };
  return codeToCountry[countryCode] || 'this country';
}


